import type { Menu } from "@circleco/compass/components/Menu";

import { t } from "@/i18n-js/instance";

type MenuOptions = React.ComponentPropsWithoutRef<typeof Menu>["options"];

interface UseEventDropdownMenuOptionsProps {
  canDestroyPost: boolean;
  canDuplicatePost: boolean;
  canManagePost: boolean;
  canSummarize: boolean;
  canUpdatePost: boolean;
  isBookmarked: boolean;
  isDeleteBlockedByPaidAttendees: boolean;
  isEventShareableViaBroadcast: boolean;
  isPinned: boolean;
  isSendingAnnouncement: boolean;
  onBookmarkToggle: () => void;
  onDeleteClick: () => void;
  onDuplicateClick: () => void;
  onEditClick: () => void;
  onPinClick?: () => void;
  onSendAnnouncementClick: () => void;
  onShareClick: () => void;
  onSummarizeClick: () => void;
  shouldShowShareInMenu: boolean;
}

type MenuEntry = MenuOptions[number];

interface ConditionalEntry {
  entry: MenuEntry;
  show: boolean;
}

export const useEventDropdownMenuOptions = ({
  isBookmarked,
  onBookmarkToggle,
  shouldShowShareInMenu,
  onShareClick,
  canUpdatePost,
  onEditClick,
  canDuplicatePost,
  onDuplicateClick,
  canManagePost,
  isPinned,
  onPinClick,
  canDestroyPost,
  isDeleteBlockedByPaidAttendees,
  onDeleteClick,
  isEventShareableViaBroadcast,
  onSendAnnouncementClick,
  isSendingAnnouncement,
  canSummarize,
  onSummarizeClick,
}: UseEventDropdownMenuOptionsProps): MenuOptions => {
  const entries: ConditionalEntry[] = [
    {
      entry: {
        label: t("post.summarize"),
        icon: "sparkle",
        onClick: onSummarizeClick,
      },
      show: canSummarize,
    },
    {
      entry: {
        label: isBookmarked
          ? t("bookmarks.actions.remove")
          : t("bookmarks.actions.add_by_type.event"),
        icon: isBookmarked ? "bookmark-filled" : "bookmark",
        onClick: onBookmarkToggle,
      },
      show: true,
    },
    {
      entry: {
        label: t("events.event_actions_dropdown.share_event"),
        icon: "arrow-share",
        onClick: onShareClick,
      },
      show: shouldShowShareInMenu,
    },
    {
      entry: {
        label: t("edit_event"),
        icon: "edit",
        onClick: onEditClick,
      },
      show: canUpdatePost,
    },
    {
      entry: {
        label: t("duplicate_event"),
        icon: "copy",
        onClick: onDuplicateClick,
      },
      show: canDuplicatePost,
    },
    {
      entry: {
        label: isPinned ? t("unpin_from_space") : t("pin_to_space"),
        icon: isPinned ? "unpin" : "pin",
        onClick: onPinClick,
      },
      show: canManagePost && !!onPinClick,
    },
    {
      entry: {
        label: t("delete_event"),
        icon: "trash-can",
        onClick: onDeleteClick,
        danger: true,
      },
      show: canDestroyPost || isDeleteBlockedByPaidAttendees,
    },
    {
      entry: "divider",
      show: isEventShareableViaBroadcast,
    },
    {
      entry: {
        label: t("share_via_broadcast"),
        icon: "megaphone",
        onClick: onSendAnnouncementClick,
        disabled: isSendingAnnouncement,
      },
      show: isEventShareableViaBroadcast,
    },
  ];

  return entries.filter(({ show }) => show).map(({ entry }) => entry);
};
